import { ReactNode, useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useRole } from '../../context/RoleContext';
import { useTheme } from '../../context/ThemeContext';
import { useAccent } from '../../context/AccentContext';
import { useT } from '../../i18n';
import { ProfileMenu } from '../ProfileMenu';
import { PendingEmailsButton } from '../PendingEmailsButton';
import { RecentProjects } from './RecentProjects';
import { InboxBadge } from './InboxBadge';
import { SessionRecorder } from './SessionRecorder';
import { CollabLayer } from '../../live/Collab';
import { NotificationBell, type NotificationBellLabels } from 'notification-kit/client/index.js';

type Page = 'home' | 'portfolio' | 'pipeline' | 'inbox' | 'projects' | 'people' | 'infrastructure' | 'deliverables' | 'admin' | 'submit' | 'profile' | 'tasks' | 'tasks-calendar' | 'tasks-kanban' | 'tasks-planning' | 'project' | 'council' | 'gerance' | 'proposals';

interface AppShellProps {
  children: ReactNode;
  currentPage: Page;
  onNavigate: (page: Page) => void;
}

const PAGE_TITLE_KEYS: Record<Page, string> = {
  home: 'page.home',
  portfolio: 'page.portfolio',
  pipeline: 'page.pipeline',
  inbox: 'page.inbox',
  projects: 'page.projects',
  people: 'page.people',
  infrastructure: 'page.infrastructure',
  deliverables: 'page.deliverables',
  admin: 'page.admin',
  submit: 'page.submit',
  profile: 'page.profile',
  tasks: 'page.tasks',
  'tasks-calendar': 'page.tasksCalendar',
  'tasks-kanban': 'page.tasksKanban',
  'tasks-planning': 'page.tasksPlanning',
  project: 'page.project',
  council: 'page.council',
  gerance: 'page.gerance',
  proposals: 'page.proposals',
};

const INVENTORY_PAGES: Page[] = ['projects', 'people', 'infrastructure', 'deliverables'];

const TASKS_PAGES: Page[] = ['tasks', 'tasks-calendar', 'tasks-planning', 'tasks-kanban'];

const PMO_PAGES: Page[] = ['council', 'gerance'];

export function AppShell({ children, currentPage, onNavigate }: AppShellProps) {
  const { user, logout, impersonatedBy, stopImpersonating } = useAuth();
  const { isAdmin, canSeeTasks, canSeeProposals, role } = useRole();
  const { theme, toggleTheme } = useTheme();
  const { logoUrl } = useAccent();
  const { t } = useT();
  const notifBellLabels: NotificationBellLabels = {
    bellTitle: t('notifBell.bellTitle'),
    dropdownTitle: t('notifBell.dropdownTitle'),
    markAllRead: t('notifBell.markAllRead'),
    empty: t('notifBell.empty'),
    justNow: t('notifBell.justNow'),
    minutesAgo: t('notifBell.minutesAgo'),
    hoursAgo: t('notifBell.hoursAgo'),
    daysAgo: t('notifBell.daysAgo'),
  };
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const stored = localStorage.getItem('sidebarCollapsed');
    return stored === 'true';
  });
  const [inventoriesOpen, setInventoriesOpen] = useState(() => {
    const stored = localStorage.getItem('inventoriesOpen');
    return stored === 'true'; // default collapsed
  });
  const [tasksOpen, setTasksOpen] = useState(() => {
    const stored = localStorage.getItem('tasksOpen');
    return stored !== 'false'; // default open
  });
  const [pmoOpen, setPmoOpen] = useState(() => {
    const stored = localStorage.getItem('pmoOpen');
    return stored !== 'false'; // default open
  });
  const [stoppingImpersonation, setStoppingImpersonation] = useState(false);

  // Wait for the server before reloading: a reload cancels a request still in flight,
  // and the reloaded page could still read the old session.
  const handleStopImpersonating = async () => {
    setStoppingImpersonation(true);
    try {
      await stopImpersonating();
    } catch (err) {
      // "Not currently impersonating": already stopped in another tab, so just reload.
      const message = (err as Error).message;
      if (message !== 'Not currently impersonating') {
        setStoppingImpersonation(false);
        alert(message);
        return;
      }
    }
    window.location.reload();
  };

  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', String(sidebarCollapsed));
  }, [sidebarCollapsed]);

  useEffect(() => {
    localStorage.setItem('inventoriesOpen', String(inventoriesOpen));
  }, [inventoriesOpen]);

  useEffect(() => {
    localStorage.setItem('tasksOpen', String(tasksOpen));
  }, [tasksOpen]);

  useEffect(() => {
    localStorage.setItem('pmoOpen', String(pmoOpen));
  }, [pmoOpen]);


  // Auto-expand Inventories when navigating to a sub-page
  useEffect(() => {
    if (INVENTORY_PAGES.includes(currentPage)) {
      setInventoriesOpen(true);
    }
  }, [currentPage]);

  // Auto-expand Tasks when navigating to a sub-page
  useEffect(() => {
    if (TASKS_PAGES.includes(currentPage)) {
      setTasksOpen(true);
    }
  }, [currentPage]);

  // Auto-expand PMO when navigating to a sub-page
  useEffect(() => {
    if (PMO_PAGES.includes(currentPage)) {
      setPmoOpen(true);
    }
  }, [currentPage]);

  const navItem = (page: Page, label: string, extra?: React.ReactNode) => (
    <a
      href={`#${page}`}
      className={`nav-link ${currentPage === page ? 'active' : ''}${extra ? ' has-badge' : ''}`}
      onClick={(e) => {
        e.preventDefault();
        onNavigate(page);
      }}
    >
      <span className="nav-link-label">{label}</span>
      {extra}
    </a>
  );

  return (
    <>
      {/* ── Topbar ── */}
      <header className="topbar">
        <div className="topbar-left">
          <div className="brand">
            {logoUrl && (
              <img src={logoUrl} alt="" className="brand-logo" onClick={() => onNavigate('home')} />
            )}
            <span className="brand-mark" onClick={() => onNavigate('home')} style={{ cursor: 'pointer' }}>PMO</span>
            <span className="brand-sep" />
            <span className="brand-page">{t(PAGE_TITLE_KEYS[currentPage])}</span>
          </div>
        </div>
        <div className="topbar-right">
          {/* Record a session (Speakr): a tab that slides the recorder out beneath it */}
          <SessionRecorder
            currentProjectId={currentPage === 'project' ? Number(new URLSearchParams(window.location.hash.split('?')[1] || '').get('id')) || null : null}
          />
          <PendingEmailsButton />
          <NotificationBell
            onNavigate={(hash) => { window.location.hash = hash; }}
            labels={notifBellLabels}
          />
          <button className="theme-btn" onClick={toggleTheme} title={theme === 'light' ? t('common.theme.toDark') : t('common.theme.toLight')}>
            {theme === 'light' ? '☾' : '☀'}
          </button>
          <ProfileMenu onOpenProfile={() => onNavigate('profile')} />
          {isAdmin && (
            <span
              className="badge badge-accent badge-clickable"
              onClick={() => onNavigate('admin')}
              title={t('shell.adminBadge')}
            >
              {t('shell.adminBadge')}
            </span>
          )}
          {role === 'direction' && (
            <span className="badge badge-direction" title={t('shell.directionBadge')}>
              {t('shell.directionBadge')}
            </span>
          )}
          <button className="btn btn-ghost btn-sm" onClick={logout}>
            {t('common.logout')}
          </button>
        </div>
      </header>

      {/* ── Shell ── */}
      <div className={`shell ${sidebarCollapsed ? 'shell--collapsed' : ''}`}>
        <aside className="sidebar">
          <button
            className="sidebar-toggle"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          >
            {sidebarCollapsed ? '›' : t('shell.collapse')}
          </button>

          {!sidebarCollapsed && (
            <nav className="sidebar-nav">
              {/* Vue d'ensemble — every project at a glance */}
              <div className="nav-section">
                <div className="nav-section-title">{t('shell.nav.overview')}</div>
                {navItem('home', t('shell.nav.home'))}
                {navItem('pipeline', t('shell.nav.pipeline'))}
                {navItem('portfolio', t('shell.nav.portfolio'))}
                {isAdmin ? navItem('inbox', t('shell.nav.inbox'), <InboxBadge onInbox={currentPage === 'inbox'} />) : navItem('submit', t('shell.nav.submit'))}
                {role === 'direction' && (
                  <a
                    href="#inbox?tab=changes"
                    className={`nav-link ${currentPage === 'inbox' && window.location.hash.includes('tab=changes') ? 'active' : ''}`}
                    onClick={(e) => {
                      e.preventDefault();
                      window.location.hash = 'inbox?tab=changes';
                    }}
                  >
                    {t('shell.nav.myChanges')}
                  </a>
                )}
              </div>

              {/* The last 3 projects opened — quick way back to a project's Aperçu */}
              <RecentProjects currentProjectId={currentPage === 'project' ? Number(new URLSearchParams(window.location.hash.split('?')[1] || '').get('id')) || null : null} />

              {/* Tasks collapsible group */}
              {canSeeTasks && (
                <div className="nav-section">
                  <div
                    className="nav-section-title nav-section-title-clickable"
                    onClick={() => setTasksOpen(!tasksOpen)}
                  >
                    <span>{t('shell.nav.tasks')}</span>
                    <span className="nav-dropdown-arrow">
                      {tasksOpen ? '▾' : '▸'}
                    </span>
                  </div>
                  {tasksOpen && (
                    <>
                      {navItem('tasks', t('shell.nav.tasksProjects'))}
                      {navItem('tasks-planning', t('shell.nav.tasksPlanning'))}
                      {navItem('tasks-kanban', t('shell.nav.tasksKanban'))}
                      {navItem('tasks-calendar', t('shell.nav.tasksCalendar'))}
                    </>
                  )}
                </div>
              )}

              {/* PMO. Admins get the collapsible group (Gérance + Conseil).
                  Non-admins who can see tasks get a plain top-level Gérance
                  link instead — no "PMO" grouping, since Gérance is the only
                  thing in it for them and the group label reads as access
                  they don't really have. */}
              {isAdmin ? (
                <div className="nav-section">
                  <div
                    className="nav-section-title nav-section-title-clickable"
                    onClick={() => setPmoOpen(!pmoOpen)}
                  >
                    <span>{t('shell.nav.pmo')}</span>
                    <span className="nav-dropdown-arrow">
                      {pmoOpen ? '▾' : '▸'}
                    </span>
                  </div>
                  {pmoOpen && (
                    <>
                      {navItem('gerance', t('shell.nav.gerance'))}
                      {navItem('council', t('shell.nav.council'))}
                    </>
                  )}
                </div>
              ) : canSeeTasks ? (
                <div className="nav-section">
                  {navItem('gerance', t('shell.nav.gerance'))}
                </div>
              ) : null}

              {/* Inventories collapsible group — admin only */}
              {isAdmin && (
                <div className="nav-section">
                  <div
                    className="nav-section-title nav-section-title-clickable"
                    onClick={() => setInventoriesOpen(!inventoriesOpen)}
                  >
                    <span>{t('shell.nav.inventories')}</span>
                    <span className="nav-dropdown-arrow">
                      {inventoriesOpen ? '▾' : '▸'}
                    </span>
                  </div>
                  {inventoriesOpen && (
                    <>
                      {navItem('projects', t('shell.nav.projects'))}
                      {navItem('people', t('shell.nav.people'))}
                      {navItem('infrastructure', t('shell.nav.infrastructure'))}
                      {navItem('deliverables', t('shell.nav.deliverables'))}
                    </>
                  )}
                </div>
              )}
            </nav>
          )}

        </aside>

        <main className="content">
          {impersonatedBy && (
            <div className="impersonation-banner">
              <span>{t('shell.impersonating', { name: user?.name ?? '', by: impersonatedBy.name })}</span>
              <button className="btn btn-sm" disabled={stoppingImpersonation} onClick={handleStopImpersonating}>
                {t('shell.stopImpersonating')}
              </button>
            </div>
          )}
          <div className="page">{children}</div>
          {/* "Mode collaborateur": follows the presenter's page and scroll; the bar at the bottom. */}
          <CollabLayer />
        </main>
      </div>
    </>
  );
}
